import React, {
    useEffect,
    useRef,
    useState,
    useCallback,
    useMemo,
} from "react";

import {
    Card,
    Button,
    Select,
    Typography,
    Space,
    Spin,
    Tag,
    Divider,
    Row,
    Col,
    Avatar,
    Alert,
    Empty,
    Grid,
} from "antd";

import {
    ArrowLeftOutlined,
    BookOutlined,
    TrophyOutlined,
    UserOutlined,
    DownloadOutlined,
    CalendarOutlined,
    FilePdfOutlined,
    BarChartOutlined,
    ReadOutlined,
} from "@ant-design/icons";

import Swal from "sweetalert2";
import api from "../../api/api";

import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

import testlogo from "./zpa.png";

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

// =====================================================
// SCHOOL COLORS
// =====================================================

const COLORS = {
    navy: "#031B35",
    deepNavy: "#021426",
    navyBlue: "#062A4B",
    blue: "#063A6B",

    gold: "#F6C944",
    goldLight: "#FFE58A",
    orange: "#C0750D",

    green: "#4FAE22",
    white: "#FFFFFF",

    page: "#F4F7F9",
    text: "#142536",
    muted: "#6B7280",
    border: "#E0E7EC",

    shadow: "0 12px 35px rgba(6,42,75,.075)",
};

const PASS_MARK = 70;

// =====================================================
// A4 PORTRAIT PDF CONFIGURATION
// A4 PORTRAIT = 210mm x 297mm
// Capture ratio matches A4 portrait.
// =====================================================

const PDF_PAGE_WIDTH_MM = 210;
const PDF_PAGE_HEIGHT_MM = 297;

const PDF_CAPTURE_WIDTH = 794;
const PDF_CAPTURE_HEIGHT = 1123;

// =====================================================
// COMPONENT
// =====================================================

const ParentsStudentGrades = ({ selectedChild, onBack }) => {

    const screens = useBreakpoint();

    const reportRef = useRef(null);

    // =================================================
    // STATES
    // =================================================

    const [periods, setPeriods] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState("");
    const [grades, setGrades] = useState([]);

    const [loading, setLoading] = useState(false);
    const [periodLoading, setPeriodLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const token = localStorage.getItem("access");
    const isMobile = !screens.md;

    // =================================================
    // SCORE HELPERS
    // =================================================

    const getNumericScore = useCallback((value) => {

        if (value === null || value === undefined || value === "") {
            return null;
        }

        const score = parseFloat(
            String(value).replace("%", "").trim()
        );

        return Number.isFinite(score) ? score : null;

    }, []);

    const getScoreStyle = useCallback((value) => {

        const score = getNumericScore(value);

        if (score === null) {
            return {
                background: "#F3F4F6",
                color: "#374151",
                border: "1px solid #D1D5DB",
            };
        }

        if (score >= PASS_MARK) {
            return {
                background: "#EAF2FA",
                color: COLORS.blue,
                border: "1px solid #B8CEE4",
            };
        }

        return {
            background: "#FFF0F0",
            color: "#B93838",
            border: "1px solid #F2C1C1",
        };

    }, [getNumericScore]);

    const getGradeLabel = useCallback((value) => {

        const score = getNumericScore(value);

        if (score === null) return "Not Available";

        return score >= PASS_MARK
            ? "Passed"
            : "Needs Improvement";

    }, [getNumericScore]);

    // =================================================
    // FETCH ACADEMIC PERIODS
    // =================================================

    useEffect(() => {

        let mounted = true;

        const fetchPeriods = async () => {

            if (!token) return;

            try {

                setPeriodLoading(true);

                const response = await api.get(
                    "/api/users/periods/",
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (mounted) {
                    setPeriods(
                        Array.isArray(response.data)
                            ? response.data
                            : []
                    );
                }

            } catch (error) {

                console.error("Academic periods error:", error);

                if (mounted) {
                    Swal.fire({
                        icon: "error",
                        title: "Unable to Load Periods",
                        text: "Unable to load academic periods.",
                        confirmButtonColor: COLORS.navyBlue,
                    });
                }

            } finally {

                if (mounted) {
                    setPeriodLoading(false);
                }

            }

        };

        fetchPeriods();

        return () => {
            mounted = false;
        };

    }, [token]);

    // =================================================
    // PERIOD CHANGE
    // =================================================

    const handlePeriodChange = (value) => {

        setSelectedPeriod(value);
        setGrades([]);

    };

    // =================================================
    // FETCH STUDENT GRADES
    // =================================================

    const fetchGrades = async () => {

        if (!selectedChild?.id) {

            Swal.fire({
                icon: "warning",
                title: "Student Required",
                text: "Please select a student first.",
                confirmButtonColor: COLORS.navyBlue,
            });

            return;
        }

        if (!selectedPeriod) return;

        try {

            setLoading(true);
            setGrades([]);

            const response = await api.get(
                "/api/users/linked-student-grades/",
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    params: {
                        period_id: selectedPeriod,
                        student_id: selectedChild.id,
                    },
                }
            );

            const data = Array.isArray(response.data)
                ? response.data
                : [];

            if (!data.length) {

                setGrades([]);

                Swal.fire({
                    icon: "info",
                    title: "No Results Available",
                    text: "Grades have not been published for this period.",
                    confirmButtonColor: COLORS.navyBlue,
                });

            } else {

                setGrades(data);

            }

        } catch (error) {

            console.error("Student grades error:", error);

            Swal.fire({
                icon: "error",
                title: "Unable to Load Grades",
                text:
                    error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    "Unable to load student grades.",
                confirmButtonColor: COLORS.navyBlue,
            });

            setGrades([]);

        } finally {

            setLoading(false);

        }

    };

    // =================================================
    // SELECTED STUDENT GRADE
    // =================================================

    const studentGrade = grades[0];

    const subjects = useMemo(() => {

        return Array.isArray(studentGrade?.student_subjects)
            ? studentGrade.student_subjects
            : [];

    }, [studentGrade]);

    // =================================================
    // PORTRAIT PDF SUBJECT SETTINGS
    // =================================================

    const pdfSettings = useMemo(() => {

        const count = subjects.length;

        if (count <= 12) {
            return {
                columns: 1,
                fontSize: 10,
                rowHeight: 25,
                gap: 5,
                padding: 8,
            };
        }

        if (count <= 30) {
            return {
                columns: 2,
                fontSize: 9,
                rowHeight: 23,
                gap: 5,
                padding: 6,
            };
        }

        if (count <= 60) {
            return {
                columns: 3,
                fontSize: 8,
                rowHeight: 20,
                gap: 4,
                padding: 5,
            };
        }

        if (count <= 100) {
            return {
                columns: 4,
                fontSize: 7,
                rowHeight: 17,
                gap: 3,
                padding: 4,
            };
        }

        return {
            columns: 5,
            fontSize: 6,
            rowHeight: 14,
            gap: 2,
            padding: 3,
        };

    }, [subjects.length]);

    // =================================================
    // SUMMARY INFORMATION
    // =================================================

    const summary = useMemo(() => {

        const passed = subjects.filter((item) => {

            const score = getNumericScore(
                item.formatted_final_score
            );

            return score !== null && score >= PASS_MARK;

        }).length;

        const needsImprovement = subjects.filter((item) => {

            const score = getNumericScore(
                item.formatted_final_score
            );

            return score !== null && score < PASS_MARK;

        }).length;

        const unavailable = subjects.length - passed - needsImprovement;

        return {
            average: studentGrade?.average_score ?? 0,
            passed,
            needsImprovement,
            unavailable,
            total: subjects.length,
        };

    }, [studentGrade, subjects, getNumericScore]);

    // =================================================
    // SAFE FILE NAME
    // =================================================

    const safeFileName = (value) => {

        return String(value || "Student")
            .trim()
            .replace(/[<>:"/\\|?*\x00-\x1F]/g, "")
            .replace(/\s+/g, "-")
            .slice(0, 90);

    };

    // =================================================
    // DOWNLOAD A4 PORTRAIT PDF
    // =================================================

    const downloadPDF = async () => {

        const element = reportRef.current;

        if (!element || !studentGrade || downloading) return;

        try {

            setDownloading(true);

            if (document.fonts?.ready) {
                await document.fonts.ready;
            }

            // Wait for all report images to load.

            const images = Array.from(
                element.querySelectorAll("img")
            );

            await Promise.all(
                images.map((img) => {

                    if (img.complete && img.naturalWidth > 0) {
                        return Promise.resolve();
                    }

                    return new Promise((resolve) => {

                        let finished = false;

                        const finish = () => {
                            if (finished) return;
                            finished = true;
                            resolve();
                        };

                        img.onload = finish;
                        img.onerror = finish;

                        setTimeout(finish, 5000);

                    });

                })
            );

            // Capture the exact portrait report dimensions.

            const canvas = await html2canvas(
                element,
                {
                    scale: 2,
                    backgroundColor: "#FFFFFF",
                    useCORS: true,
                    allowTaint: false,
                    logging: false,

                    width: PDF_CAPTURE_WIDTH,
                    height: PDF_CAPTURE_HEIGHT,

                    windowWidth: PDF_CAPTURE_WIDTH,
                    windowHeight: PDF_CAPTURE_HEIGHT,

                    scrollX: 0,
                    scrollY: 0,

                    imageTimeout: 10000,

                    onclone: (clonedDocument) => {

                        const clonedReport = clonedDocument.getElementById(
                            "student-grade-pdf-report"
                        );

                        if (clonedReport) {

                            clonedReport.style.width =
                                `${PDF_CAPTURE_WIDTH}px`;

                            clonedReport.style.height =
                                `${PDF_CAPTURE_HEIGHT}px`;

                            clonedReport.style.minHeight =
                                `${PDF_CAPTURE_HEIGHT}px`;

                            clonedReport.style.maxHeight =
                                `${PDF_CAPTURE_HEIGHT}px`;

                            clonedReport.style.overflow = "hidden";
                            clonedReport.style.display = "block";
                            clonedReport.style.boxSizing = "border-box";

                        }

                    },
                }
            );

            if (!canvas.width || !canvas.height) {
                throw new Error("The report could not be captured.");
            }

            // Create an A4 PORTRAIT PDF.

            const pdfDocument = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
                compress: true,
                precision: 12,
            });

            const imageData = canvas.toDataURL(
                "image/jpeg",
                0.96
            );

            // Fill the complete A4 portrait page.

            pdfDocument.addImage(
                imageData,
                "JPEG",
                0,
                0,
                PDF_PAGE_WIDTH_MM,
                PDF_PAGE_HEIGHT_MM,
                undefined,
                "FAST"
            );

            const studentName = safeFileName(
                studentGrade.student_name
            );

            const periodName = safeFileName(
                studentGrade.period_name || "Academic-Report"
            );

            pdfDocument.save(
                `${studentName}-${periodName}-Grade-Report.pdf`
            );

            Swal.fire({
                icon: "success",
                title: "Download Complete",
                text: "The student's academic report has been downloaded as an A4 portrait PDF.",
                timer: 2400,
                showConfirmButton: false,
            });

        } catch (error) {

            console.error("PDF generation error:", error);

            Swal.fire({
                icon: "error",
                title: "PDF Generation Failed",
                text:
                    error?.message ||
                    "Unable to generate the grade report. Please try again.",
                confirmButtonColor: COLORS.navyBlue,
            });

        } finally {

            setDownloading(false);

        }

    };

    // =================================================
    // REUSABLE SECTION HEADER
    // =================================================

    const SectionHeader = ({
        icon,
        title,
        subtitle,
        extra,
    }) => (

        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 20,
            }}
        >

            <Space align="center" size={11}>

                <div
                    style={{
                        width: 40,
                        height: 40,
                        borderRadius: 11,
                        background: "#FFF6D9",
                        color: COLORS.orange,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                    }}
                >
                    {icon}
                </div>

                <div>

                    <Text
                        strong
                        style={{
                            display: "block",
                            color: COLORS.navyBlue,
                            fontSize: 15,
                        }}
                    >
                        {title}
                    </Text>

                    {subtitle && (
                        <Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                        >
                            {subtitle}
                        </Text>
                    )}

                </div>

            </Space>

            {extra}

        </div>

    );

    // =================================================
    // SUMMARY CARD
    // =================================================

    const SummaryCard = ({
        title,
        value,
        icon,
        color,
        background,
        subtitle,
    }) => (

        <Card
            bordered={false}
            style={{
                height: "100%",
                borderRadius: 13,
                boxShadow: "0 3px 13px rgba(15,40,65,.045)",
                border: `1px solid ${COLORS.border}`,
            }}
            styles={{
                body: {
                    padding: "21px 19px",
                },
            }}
        >

            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 10,
                }}
            >

                <Text
                    type="secondary"
                    style={{
                        fontSize: 12,
                        fontWeight: 700,
                    }}
                >
                    {title}
                </Text>

                <div
                    style={{
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 10,
                        background,
                        color,
                        fontSize: 19,
                    }}
                >
                    {icon}
                </div>

            </div>

            <Title
                level={3}
                style={{
                    margin: "14px 0 5px",
                    color,
                    fontWeight: 800,
                }}
            >
                {value}
            </Title>

            <Text
                type="secondary"
                style={{ fontSize: 11 }}
            >
                {subtitle}
            </Text>

        </Card>

    );

    // =================================================
    // MAIN RENDER
    // =================================================

    return (

        <div
            style={{
                minHeight: "100vh",
                background: COLORS.page,
                padding: isMobile ? "12px 8px" : "24px",
            }}
        >

            <div
                style={{
                    maxWidth: 1250,
                    margin: "0 auto",
                }}
            >

                {/* PAGE HEADER */}

                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 15,
                        marginBottom: 22,
                    }}
                >

                    <div>

                        <Text
                            style={{
                                color: COLORS.orange,
                                fontWeight: 800,
                                fontSize: 11,
                                letterSpacing: 1.5,
                            }}
                        >
                            ACADEMIC RECORDS
                        </Text>

                        <Title
                            level={2}
                            style={{
                                color: COLORS.navyBlue,
                                margin: "5px 0",
                                fontWeight: 900,
                            }}
                        >
                            Student Grade Report
                        </Title>

                        <Text
                            type="secondary"
                            style={{ fontSize: 13 }}
                        >
                            Review academic performance and published grades.
                        </Text>

                    </div>

                    {onBack && (

                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={onBack}
                            size="large"
                            style={{
                                height: 42,
                                borderRadius: 8,
                                fontWeight: 600,
                            }}
                        >
                            Back
                        </Button>

                    )}

                </div>

                {/* MAIN CONTAINER */}

                <Card
                    bordered={false}
                    style={{
                        borderRadius: 15,
                        overflow: "hidden",
                        boxShadow: COLORS.shadow,
                    }}
                    styles={{
                        body: {
                            padding: isMobile ? 13 : 25,
                        },
                    }}
                >

                    {/* PERIOD SELECTOR */}

                    <Card
                        bordered={false}
                        style={{
                            marginBottom: 24,
                            borderRadius: 12,
                            background: "#F8FAFC",
                            border: `1px solid ${COLORS.border}`,
                        }}
                        styles={{
                            body: {
                                padding: isMobile ? 15 : 20,
                            },
                        }}
                    >

                        <SectionHeader
                            icon={<CalendarOutlined />}
                            title="Academic Period"
                            subtitle="Select the period to view the student's published grades."
                        />

                        <Row gutter={[15, 15]} align="bottom">

                            <Col xs={24} md={16}>

                                <Text
                                    strong
                                    style={{
                                        display: "block",
                                        marginBottom: 7,
                                        color: COLORS.text,
                                        fontSize: 12,
                                    }}
                                >
                                    SELECT REPORTING PERIOD
                                </Text>

                                <Select
                                    size="large"
                                    showSearch
                                    optionFilterProp="children"
                                    style={{ width: "100%" }}
                                    placeholder="Choose an academic period"
                                    value={selectedPeriod || undefined}
                                    loading={periodLoading}
                                    onChange={handlePeriodChange}
                                    notFoundContent={
                                        periodLoading
                                            ? <Spin size="small" />
                                            : "No academic periods available"
                                    }
                                >

                                    {periods.map((period) => (

                                        <Option
                                            key={period.id}
                                            value={period.id}
                                        >
                                            {period.name}
                                        </Option>

                                    ))}

                                </Select>

                            </Col>

                            <Col xs={24} md={8}>

                                <Button
                                    type="primary"
                                    block
                                    size="large"
                                    loading={loading}
                                    disabled={!selectedPeriod || periodLoading}
                                    icon={<BookOutlined />}
                                    onClick={fetchGrades}
                                    style={{
                                        height: 44,
                                        borderRadius: 8,
                                        fontWeight: 700,
                                        background: COLORS.navyBlue,
                                        borderColor: COLORS.navyBlue,
                                    }}
                                >
                                    View Grades
                                </Button>

                            </Col>

                        </Row>

                    </Card>

                    {/* LOADING */}

                    {loading && (

                        <div
                            style={{
                                padding: "65px 20px",
                                textAlign: "center",
                            }}
                        >

                            <Spin size="large" />

                            <Title
                                level={5}
                                style={{
                                    marginTop: 17,
                                    color: COLORS.navyBlue,
                                }}
                            >
                                Loading Grade Report
                            </Title>

                            <Text type="secondary">
                                Retrieving the student's academic results...
                            </Text>

                        </div>

                    )}

                    {/* EMPTY STATE */}

                    {!loading && !studentGrade && (

                        <div
                            style={{
                                padding: "40px 15px",
                                textAlign: "center",
                            }}
                        >

                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <div>
                                        <Text
                                            strong
                                            style={{
                                                display: "block",
                                                color: COLORS.text,
                                            }}
                                        >
                                            No Grade Report Available
                                        </Text>

                                        <Text
                                            type="secondary"
                                            style={{ fontSize: 12 }}
                                        >
                                            Select an academic period and click View Grades to retrieve the student's results.
                                        </Text>
                                    </div>
                                }
                            />

                        </div>

                    )}

                    {/* ON-SCREEN GRADE REPORT */}

                    {!loading && studentGrade && (

                        <div>

                            {/* SCHOOL HEADER */}

                            <div
                                style={{
                                    borderRadius: 12,
                                    overflow: "hidden",
                                    marginBottom: 22,
                                    border: `1px solid ${COLORS.gold}`,
                                    background: COLORS.white,
                                }}
                            >

                                <div
                                    style={{
                                        height: 5,
                                        background: `linear-gradient(90deg,${COLORS.deepNavy},${COLORS.gold},${COLORS.blue})`,
                                    }}
                                />

                                <div
                                    style={{
                                        padding: isMobile ? 17 : 25,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 20,
                                        flexDirection: isMobile ? "column" : "row",
                                        textAlign: isMobile ? "center" : "left",
                                    }}
                                >

                                    <Avatar
                                        src={testlogo}
                                        size={isMobile ? 78 : 95}
                                        shape="square"
                                        style={{
                                            background: COLORS.white,
                                            border: `2px solid ${COLORS.gold}`,
                                            borderRadius: 12,
                                            flexShrink: 0,
                                        }}
                                    />

                                    <div style={{ flex: 1, minWidth: 0 }}>

                                        <Text
                                            style={{
                                                color: COLORS.orange,
                                                fontSize: 10,
                                                fontWeight: 800,
                                                letterSpacing: 2,
                                            }}
                                        >
                                            OFFICIAL ACADEMIC RECORD
                                        </Text>

                                        <Title
                                            level={isMobile ? 4 : 3}
                                            style={{
                                                color: COLORS.navyBlue,
                                                margin: "5px 0",
                                                fontWeight: 900,
                                            }}
                                        >
                                            Excellent Kids Foundation
                                        </Title>

                                        <Text
                                            style={{
                                                display: "block",
                                                color: COLORS.text,
                                                fontSize: 12,
                                            }}
                                        >
                                            1st Cowfield Junction, Paynesville City, Monrovia Liberia
                                        </Text>

                                        <Text
                                            style={{
                                                display: "block",
                                                color: COLORS.text,
                                                fontSize: 12,
                                                marginTop: 4,
                                            }}
                                        >
                                            Contact: 0886909466 / 0770123025
                                        </Text>

                                        <Divider
                                            style={{
                                                margin: "12px 0",
                                                borderColor: COLORS.border,
                                            }}
                                        />

                                        <Title
                                            level={5}
                                            style={{
                                                margin: 0,
                                                color: "#946A0B",
                                                fontWeight: 800,
                                                letterSpacing: 0.4,
                                            }}
                                        >
                                            STUDENT PERIODIC REPORT
                                        </Title>

                                        <Text
                                            type="secondary"
                                            style={{
                                                display: "block",
                                                fontSize: 11,
                                                marginTop: 5,
                                            }}
                                        >
                                            Official student academic performance statement
                                        </Text>

                                    </div>

                                </div>

                            </div>

                            {/* STUDENT INFORMATION */}

                            <Card
                                bordered={false}
                                style={{
                                    borderRadius: 12,
                                    marginBottom: 22,
                                    border: `1px solid ${COLORS.border}`,
                                }}
                                styles={{
                                    body: {
                                        padding: isMobile ? 15 : 21,
                                    },
                                }}
                            >

                                <SectionHeader
                                    icon={<UserOutlined />}
                                    title="Student Information"
                                    subtitle="Student identity and reporting period details."
                                />

                                <Row gutter={[20, 20]}>

                                    <Col xs={24} md={8}>

                                        <div
                                            style={{
                                                padding: 14,
                                                borderRadius: 9,
                                                background: "#F7F9FB",
                                                height: "100%",
                                            }}
                                        >

                                            <Text
                                                type="secondary"
                                                style={{
                                                    display: "block",
                                                    fontSize: 11,
                                                    marginBottom: 7,
                                                }}
                                            >
                                                STUDENT NAME
                                            </Text>

                                            <Space size={8}>
                                                <UserOutlined style={{ color: COLORS.orange }} />

                                                <Text strong style={{ color: COLORS.navyBlue }}>
                                                    {studentGrade.student_name || "-"}
                                                </Text>
                                            </Space>

                                        </div>

                                    </Col>

                                    <Col xs={24} md={8}>

                                        <div
                                            style={{
                                                padding: 14,
                                                borderRadius: 9,
                                                background: "#F7F9FB",
                                                height: "100%",
                                            }}
                                        >

                                            <Text
                                                type="secondary"
                                                style={{
                                                    display: "block",
                                                    fontSize: 11,
                                                    marginBottom: 7,
                                                }}
                                            >
                                                GRADE CLASS
                                            </Text>

                                            <Space size={8}>
                                                <ReadOutlined style={{ color: COLORS.orange }} />

                                                <Text strong style={{ color: COLORS.navyBlue }}>
                                                    {studentGrade.grade_class_name || "-"}
                                                </Text>
                                            </Space>

                                        </div>

                                    </Col>

                                    <Col xs={24} md={8}>

                                        <div
                                            style={{
                                                padding: 14,
                                                borderRadius: 9,
                                                background: "#F7F9FB",
                                                height: "100%",
                                            }}
                                        >

                                            <Text
                                                type="secondary"
                                                style={{
                                                    display: "block",
                                                    fontSize: 11,
                                                    marginBottom: 7,
                                                }}
                                            >
                                                ACADEMIC PERIOD
                                            </Text>

                                            <Space size={8}>
                                                <CalendarOutlined style={{ color: COLORS.orange }} />

                                                <Text strong style={{ color: COLORS.navyBlue }}>
                                                    {studentGrade.period_name || "-"}
                                                </Text>
                                            </Space>

                                        </div>

                                    </Col>

                                </Row>

                            </Card>

                            {/* SUBJECT PERFORMANCE */}

                            <Card
                                bordered={false}
                                style={{
                                    borderRadius: 12,
                                    border: `1px solid ${COLORS.border}`,
                                }}
                                styles={{
                                    body: {
                                        padding: isMobile ? 13 : 21,
                                    },
                                }}
                            >

                                <SectionHeader
                                    icon={<BookOutlined />}
                                    title="Subject Performance"
                                    subtitle="Published final scores for each subject or course."
                                    extra={
                                        <Tag
                                            style={{
                                                margin: 0,
                                                background: "#EDF5FC",
                                                color: COLORS.blue,
                                                border: "none",
                                                padding: "5px 10px",
                                                borderRadius: 6,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {subjects.length} Subjects
                                        </Tag>
                                    }
                                />

                                {subjects.length === 0 ? (

                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description="No subject records available."
                                    />

                                ) : (

                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr",
                                            gap: 8,
                                        }}
                                    >

                                        {subjects.map((item, index) => {

                                            const style = getScoreStyle(
                                                item.formatted_final_score
                                            );

                                            return (

                                                <div
                                                    key={item.id || index}
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        gap: 10,
                                                        padding: "11px 13px",
                                                        borderRadius: 8,
                                                        border: `1px solid ${COLORS.border}`,
                                                        background: index % 2 === 0
                                                            ? "#FAFCFE"
                                                            : "#FFFFFF",
                                                    }}
                                                >

                                                    <Space size={10}>
                                                        <BookOutlined style={{ color: COLORS.orange }} />

                                                        <Text strong style={{ color: COLORS.text }}>
                                                            {item.subject_name || "-"}
                                                        </Text>
                                                    </Space>

                                                    <Space size={10}>

                                                        <Tag
                                                            style={{
                                                                ...style,
                                                                margin: 0,
                                                                minWidth: 65,
                                                                textAlign: "center",
                                                                fontWeight: 800,
                                                                padding: "4px 10px",
                                                                borderRadius: 6,
                                                            }}
                                                        >
                                                            {item.formatted_final_score ?? "-"}
                                                        </Tag>

                                                        <Tag
                                                            color={
                                                                getNumericScore(item.formatted_final_score) === null
                                                                    ? "default"
                                                                    : getNumericScore(item.formatted_final_score) >= PASS_MARK
                                                                        ? "success"
                                                                        : "error"
                                                            }
                                                        >
                                                            {getGradeLabel(item.formatted_final_score)}
                                                        </Tag>

                                                    </Space>

                                                </div>

                                            );

                                        })}

                                    </div>

                                )}

                                <Divider style={{ margin: "18px 0 13px" }} />

                                <Space wrap size={18}>

                                    <Space size={7}>
                                        <span
                                            style={{
                                                width: 9,
                                                height: 9,
                                                borderRadius: "50%",
                                                background: COLORS.blue,
                                            }}
                                        />

                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            70 and above: Passed
                                        </Text>
                                    </Space>

                                    <Space size={7}>
                                        <span
                                            style={{
                                                width: 9,
                                                height: 9,
                                                borderRadius: "50%",
                                                background: "#B93838",
                                            }}
                                        />

                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            Below 70: Needs Improvement
                                        </Text>
                                    </Space>

                                </Space>

                            </Card>

                            {/* PERFORMANCE SUMMARY */}

                            <div style={{ marginTop: 24 }}>

                                <SectionHeader
                                    icon={<BarChartOutlined />}
                                    title="Academic Performance Summary"
                                    subtitle="Overall performance indicators for this reporting period."
                                />

                                <Row gutter={[16, 16]}>

                                    <Col xs={24} md={8}>

                                        <SummaryCard
                                            title="AVERAGE SCORE"
                                            value={studentGrade.average_score ?? 0}
                                            icon={<TrophyOutlined />}
                                            color={COLORS.orange}
                                            background="#FFF6D9"
                                            subtitle={getGradeLabel(studentGrade.average_score)}
                                        />

                                    </Col>

                                    <Col xs={24} md={8}>

                                        <SummaryCard
                                            title="CLASS RANK"
                                            value={studentGrade.rank ?? "-"}
                                            icon={<TrophyOutlined />}
                                            color={COLORS.blue}
                                            background="#EDF5FC"
                                            subtitle="Position in class"
                                        />

                                    </Col>

                                    <Col xs={24} md={8}>

                                        <SummaryCard
                                            title="TOTAL SUBJECTS"
                                            value={subjects.length}
                                            icon={<BookOutlined />}
                                            color={COLORS.green}
                                            background="#EAF7EF"
                                            subtitle="Published results"
                                        />

                                    </Col>

                                </Row>

                            </div>

                            {/* TEACHER REMARK */}

                            <Card
                                bordered={false}
                                style={{
                                    marginTop: 24,
                                    borderRadius: 12,
                                    border: `1px solid ${COLORS.border}`,
                                }}
                                styles={{
                                    body: {
                                        padding: isMobile ? 15 : 21,
                                    },
                                }}
                            >

                                <SectionHeader
                                    icon={<FilePdfOutlined />}
                                    title="Teacher's Remark"
                                    subtitle="Comments concerning the student's academic performance."
                                />

                                <Alert
                                    message={
                                        studentGrade.remark ||
                                        "No remark provided."
                                    }
                                    type="info"
                                    showIcon
                                    style={{
                                        borderRadius: 9,
                                        padding: "13px 16px",
                                    }}
                                />

                            </Card>

                        </div>

                    )}

                    {/* DOWNLOAD BUTTON */}

                    {!loading && studentGrade && (

                        <div
                            style={{
                                marginTop: 22,
                                paddingTop: 20,
                                borderTop: `1px solid ${COLORS.border}`,
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 12,
                            }}
                        >

                            <Text
                                type="secondary"
                                style={{
                                    fontSize: 12,
                                    marginRight: "auto",
                                }}
                            >
                                Download the complete academic report in a single A4 portrait PDF page.
                            </Text>

                            <Button
                                type="primary"
                                size="large"
                                icon={<DownloadOutlined />}
                                loading={downloading}
                                onClick={downloadPDF}
                                style={{
                                    height: 46,
                                    borderRadius: 8,
                                    padding: "0 25px",
                                    fontWeight: 700,
                                    background: COLORS.navyBlue,
                                    borderColor: COLORS.navyBlue,
                                }}
                            >
                                {downloading
                                    ? "Preparing PDF..."
                                    : "Download Grade PDF"}
                            </Button>

                        </div>

                    )}

                </Card>

            </div>

            {/* =====================================================
                DEDICATED A4 PORTRAIT PDF REPORT

                PAGE: 210mm x 297mm
                CAPTURE: 794px x 1123px

                CONTENT ORDER:
                1. School header
                2. Student information
                3. Subject performance
                4. Academic summary
                5. Teacher's remark
                6. Footer

                All sections use normal document flow.
                No flex-grow positioning or detached footer.
            ===================================================== */}

            {!loading && studentGrade && (

                <div
                    style={{
                        position: "fixed",
                        left: "-12000px",
                        top: 0,
                        width: `${PDF_CAPTURE_WIDTH}px`,
                        height: `${PDF_CAPTURE_HEIGHT}px`,
                        zIndex: -9999,
                        pointerEvents: "none",
                    }}
                >

                    <div
                        id="student-grade-pdf-report"
                        ref={reportRef}
                        style={{
                            width: `${PDF_CAPTURE_WIDTH}px`,
                            height: `${PDF_CAPTURE_HEIGHT}px`,
                            minHeight: `${PDF_CAPTURE_HEIGHT}px`,
                            maxHeight: `${PDF_CAPTURE_HEIGHT}px`,
                            boxSizing: "border-box",
                            padding: "25px 32px 20px",
                            background: "#FFFFFF",
                            color: COLORS.text,
                            fontFamily: "Arial, Helvetica, sans-serif",
                            display: "block",
                            overflow: "hidden",
                            position: "relative",
                        }}
                    >

                        {/* TOP BORDER */}

                        <div
                            style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top: 0,
                                height: 8,
                                background: `linear-gradient(90deg,${COLORS.deepNavy},${COLORS.blue},${COLORS.gold})`,
                            }}
                        />

                        {/* 1. SCHOOL HEADER */}

                        <div
                            style={{
                                minHeight: 145,
                                boxSizing: "border-box",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 18,
                                borderBottom: `2px solid ${COLORS.gold}`,
                                padding: "9px 0 14px",
                            }}
                        >

                            <img
                                src={testlogo}
                                crossOrigin="anonymous"
                                alt="Excellent Kids Foundation Logo"
                                style={{
                                    width: 105,
                                    height: 105,
                                    objectFit: "contain",
                                    border: `2px solid ${COLORS.gold}`,
                                    borderRadius: 10,
                                    padding: 4,
                                    background: "#FFFFFF",
                                    flexShrink: 0,
                                }}
                            />

                            <div
                                style={{
                                    textAlign: "center",
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >

                                <div
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 900,
                                        color: COLORS.orange,
                                        letterSpacing: 2,
                                        marginBottom: 5,
                                    }}
                                >
                                    OFFICIAL ACADEMIC RECORD
                                </div>

                                <div
                                    style={{
                                        fontSize: 24,
                                        fontWeight: 900,
                                        color: COLORS.navyBlue,
                                        letterSpacing: 0.4,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    EXCELLENT KIDS FOUNDATION
                                </div>

                                <div
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 800,
                                        color: COLORS.blue,
                                        marginTop: 4,
                                        letterSpacing: 1,
                                    }}
                                >
                                    CRM SCHOOL SYSTEM
                                </div>

                                <div
                                    style={{
                                        fontSize: 9,
                                        color: "#536477",
                                        marginTop: 6,
                                    }}
                                >
                                    1st Cowfield Junction, Paynesville City, Monrovia Liberia
                                </div>

                                <div
                                    style={{
                                        fontSize: 9,
                                        color: "#536477",
                                        marginTop: 3,
                                    }}
                                >
                                    Contact: 0886909466 / 0770123025
                                </div>

                                <div
                                    style={{
                                        display: "inline-block",
                                        background: COLORS.deepNavy,
                                        color: COLORS.gold,
                                        fontSize: 10,
                                        fontWeight: 900,
                                        letterSpacing: 1,
                                        padding: "7px 24px",
                                        borderRadius: 4,
                                        marginTop: 9,
                                    }}
                                >
                                    STUDENT PERIODIC REPORT
                                </div>

                            </div>

                        </div>

                        {/* 2. STUDENT INFORMATION */}

                        <div
                            style={{
                                marginTop: 17,
                            }}
                        >

                            <div
                                style={{
                                    fontSize: 11,
                                    fontWeight: 900,
                                    color: COLORS.navyBlue,
                                    letterSpacing: 1,
                                    marginBottom: 9,
                                }}
                            >
                                STUDENT INFORMATION
                            </div>

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1.4fr 1fr 1fr",
                                    gap: 9,
                                }}
                            >

                                {[
                                    {
                                        label: "STUDENT NAME",
                                        value: studentGrade.student_name,
                                    },
                                    {
                                        label: "GRADE CLASS",
                                        value: studentGrade.grade_class_name,
                                    },
                                    {
                                        label: "ACADEMIC PERIOD",
                                        value: studentGrade.period_name,
                                    },
                                ].map((item, index) => (

                                    <div
                                        key={index}
                                        style={{
                                            minHeight: 61,
                                            boxSizing: "border-box",
                                            background: "#F4F7FA",
                                            border: "1px solid #DCE4EC",
                                            borderRadius: 5,
                                            padding: "9px 10px",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            overflow: "hidden",
                                        }}
                                    >

                                        <div
                                            style={{
                                                fontSize: 8,
                                                color: COLORS.muted,
                                                fontWeight: 900,
                                                letterSpacing: 0.5,
                                                marginBottom: 6,
                                            }}
                                        >
                                            {item.label}
                                        </div>

                                        <div
                                            style={{
                                                fontSize: 10,
                                                color: COLORS.navyBlue,
                                                fontWeight: 900,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {item.value || "-"}
                                        </div>

                                    </div>

                                ))}

                            </div>

                        </div>

                        {/* 3. SUBJECT PERFORMANCE */}

                        <div
                            style={{
                                marginTop: 17,
                            }}
                        >

                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: 9,
                                }}
                            >

                                <div
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 900,
                                        color: COLORS.navyBlue,
                                        letterSpacing: 1,
                                    }}
                                >
                                    SUBJECT PERFORMANCE
                                </div>

                                <div
                                    style={{
                                        fontSize: 9,
                                        fontWeight: 900,
                                        color: COLORS.blue,
                                        background: "#EDF5FC",
                                        padding: "5px 10px",
                                        borderRadius: 4,
                                    }}
                                >
                                    TOTAL SUBJECTS: {subjects.length}
                                </div>

                            </div>

                            {/* SUBJECT TABLE HEADERS */}

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: `repeat(${pdfSettings.columns}, minmax(0, 1fr))`,
                                    gap: pdfSettings.gap,
                                    marginBottom: 5,
                                }}
                            >

                                {Array.from(
                                    { length: pdfSettings.columns },
                                    (_, index) => (

                                        <div
                                            key={index}
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "minmax(0, 1fr) 48px 58px",
                                                alignItems: "center",
                                                gap: 4,
                                                background: COLORS.deepNavy,
                                                color: "#FFFFFF",
                                                padding: "7px 7px",
                                                borderRadius: 3,
                                                fontSize: 8,
                                                fontWeight: 900,
                                            }}
                                        >

                                            <span>SUBJECT</span>

                                            <span style={{ textAlign: "center" }}>
                                                SCORE
                                            </span>

                                            <span style={{ textAlign: "right" }}>
                                                STATUS
                                            </span>

                                        </div>

                                    )
                                )}

                            </div>

                            {/* SUBJECT ROWS */}

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: `repeat(${pdfSettings.columns}, minmax(0, 1fr))`,
                                    gap: `0 ${pdfSettings.gap}px`,
                                    alignItems: "start",
                                }}
                            >

                                {subjects.map((item, index) => {

                                    const score = item.formatted_final_score ?? "-";

                                    const numericScore = getNumericScore(score);

                                    const passed =
                                        numericScore !== null &&
                                        numericScore >= PASS_MARK;

                                    const scoreStyle = getScoreStyle(score);

                                    return (

                                        <div
                                            key={item.id || index}
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "minmax(0, 1fr) 48px 58px",
                                                alignItems: "center",
                                                gap: 4,
                                                height: pdfSettings.rowHeight,
                                                boxSizing: "border-box",
                                                padding: `0 ${pdfSettings.padding}px`,
                                                background: index % 2 === 0
                                                    ? "#F5F8FC"
                                                    : "#FFFFFF",
                                                border: "1px solid #E4EAF0",
                                                borderRadius: 4,
                                                overflow: "hidden",
                                                marginBottom: 4,
                                                minWidth: 0,
                                            }}
                                        >

                                            <span
                                                style={{
                                                    fontSize: pdfSettings.fontSize,
                                                    fontWeight: 700,
                                                    color: COLORS.navyBlue,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {index + 1}. {item.subject_name || "Unnamed Subject"}
                                            </span>

                                            <span
                                                style={{
                                                    textAlign: "center",
                                                    fontSize: pdfSettings.fontSize,
                                                    fontWeight: 900,
                                                    color: scoreStyle.color,
                                                    background: scoreStyle.background,
                                                    border: scoreStyle.border,
                                                    borderRadius: 3,
                                                    padding: "3px 2px",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {score}
                                            </span>

                                            <span
                                                style={{
                                                    textAlign: "right",
                                                    fontSize: Math.max(
                                                        6,
                                                        pdfSettings.fontSize - 1
                                                    ),
                                                    fontWeight: 900,
                                                    color: numericScore === null
                                                        ? COLORS.muted
                                                        : passed
                                                            ? COLORS.green
                                                            : "#B93838",
                                                }}
                                            >
                                                {numericScore === null
                                                    ? "N/A"
                                                    : passed
                                                        ? "PASS"
                                                        : "BELOW"}
                                            </span>

                                        </div>

                                    );

                                })}

                                {subjects.length === 0 && (

                                    <div
                                        style={{
                                            gridColumn: "1 / -1",
                                            textAlign: "center",
                                            padding: 12,
                                            color: COLORS.muted,
                                            fontSize: 10,
                                        }}
                                    >
                                        No subject records available.
                                    </div>

                                )}

                            </div>

                        </div>

                        {/* 4. ACADEMIC PERFORMANCE SUMMARY */}

                        <div
                            style={{
                                marginTop: 17,
                            }}
                        >

                            <div
                                style={{
                                    fontSize: 11,
                                    fontWeight: 900,
                                    color: COLORS.navyBlue,
                                    letterSpacing: 1,
                                    marginBottom: 9,
                                }}
                            >
                                ACADEMIC PERFORMANCE SUMMARY
                            </div>

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                    gap: 8,
                                }}
                            >

                                {[
                                    {
                                        title: "AVERAGE SCORE",
                                        value: studentGrade.average_score ?? 0,
                                        color: COLORS.orange,
                                    },
                                    {
                                        title: "CLASS RANK",
                                        value: studentGrade.rank ?? "-",
                                        color: COLORS.blue,
                                    },
                                    {
                                        title: "SUBJECTS PASSED",
                                        value: summary.passed,
                                        color: COLORS.green,
                                    },
                                    {
                                        title: "NEEDS IMPROVEMENT",
                                        value: summary.needsImprovement,
                                        color: "#B93838",
                                    },
                                ].map((item, index) => (

                                    <div
                                        key={index}
                                        style={{
                                            minHeight: 53,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            boxSizing: "border-box",
                                            background: "#F7F9FC",
                                            border: "1px solid #E3E9EF",
                                            borderRadius: 5,
                                            padding: "0 13px",
                                            gap: 8,
                                        }}
                                    >

                                        <span
                                            style={{
                                                fontSize: 9,
                                                color: COLORS.muted,
                                                fontWeight: 900,
                                                letterSpacing: 0.3,
                                            }}
                                        >
                                            {item.title}
                                        </span>

                                        <span
                                            style={{
                                                fontSize: 18,
                                                color: item.color,
                                                fontWeight: 900,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {item.value}
                                        </span>

                                    </div>

                                ))}

                            </div>

                        </div>

                        {/* 5. TEACHER REMARK */}

                        <div
                            style={{
                                marginTop: 15,
                                minHeight: 75,
                                boxSizing: "border-box",
                                padding: "11px 13px",
                                border: "1px solid #DCE4EC",
                                borderLeft: `4px solid ${COLORS.gold}`,
                                borderRadius: 4,
                                background: "#FAFCFE",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                gap: 7,
                            }}
                        >

                            <div
                                style={{
                                    fontSize: 9,
                                    fontWeight: 900,
                                    color: COLORS.navyBlue,
                                    letterSpacing: 0.5,
                                }}
                            >
                                TEACHER'S REMARK
                            </div>

                            <div
                                style={{
                                    fontSize: 10,
                                    color: COLORS.text,
                                    fontWeight: 600,
                                    lineHeight: 1.4,
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {studentGrade.remark || "No remark provided."}
                            </div>

                        </div>

                        {/* 6. REPORT FOOTER */}

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderTop: `1px solid ${COLORS.border}`,
                                paddingTop: 9,
                                marginTop: 13,
                            }}
                        >

                            <div
                                style={{
                                    fontSize: 8,
                                    color: COLORS.muted,
                                    fontWeight: 800,
                                    letterSpacing: 0.2,
                                }}
                            >
                                EXCELLENT KIDS FOUNDATION • OFFICIAL STUDENT ACADEMIC REPORT
                            </div>

                            <div
                                style={{
                                    fontSize: 8,
                                    color: COLORS.orange,
                                    fontWeight: 900,
                                    letterSpacing: 0.5,
                                }}
                            >
                                END OF REPORT
                            </div>

                        </div>

                    </div>

                </div>

            )}

            {/* PAGE STYLING */}

            <style>
                {`
                    .ant-select-selector {
                        border-radius: 8px !important;
                    }

                    .ant-btn-primary {
                        box-shadow: 0 5px 15px rgba(3,27,53,.12);
                    }

                    .ant-btn-primary:hover {
                        background: ${COLORS.blue} !important;
                        border-color: ${COLORS.blue} !important;
                    }

                    @media print {
                        body {
                            background: #FFFFFF !important;
                        }
                    }
                `}
            </style>

        </div>

    );

};

export default ParentsStudentGrades;